import React, { useCallback, useState } from 'react';
import { StanCard, StanDropdownMenu, StanTag } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { useModal } from '@/hooks/useModal';
import type { SubscriptionViewModel } from '../hooks/useCustomerSubscriptions';
import { ManageMembershipModal } from './ManageMembershipModal';
import { useCustomerActions } from '../queries/useCustomerActions';
import { LuEllipsisVertical } from 'react-icons/lu';
import { cn } from '@/lib/utils/cn';
import type { CustomerProfile, ManageMembershipPayload } from '../types/customer';
import { stanNotify } from '@/lib';
import { SENTRY_FEATURES } from '@/lib/constants';

interface CustomerSubscriptionsSectionProps {
    subscriptions: SubscriptionViewModel[];
    customer: CustomerProfile;
    onCustomerUpdated?: () => void;
}

export function CustomerSubscriptionsSection({
    subscriptions,
    customer,
    onCustomerUpdated,
}: CustomerSubscriptionsSectionProps) {
    // Modal state
    const membershipModal = useModal();

    // Local state for membership action
    const [membershipAction, setMembershipAction] = useState<{
        subscription: SubscriptionViewModel;
        action: ManageMembershipPayload['action'];
        description: React.ReactNode;
    } | null>(null);

    // Customer actions
    const { copyBillingLinkMutation, manageMembershipMutation } = useCustomerActions(customer.id);

    // Memoized action handlers
    const handleSubscriptionAction = useCallback(
        async (subscription: SubscriptionViewModel, actionId: string) => {
            if (actionId === 'copyBilling') {
                try {
                    await copyBillingLinkMutation.mutateAsync(subscription.subscriptionId);
                    stanNotify({ type: 'success', title: 'URL copied!', duration: 2000 });
                } catch {
                    stanNotify({
                        type: 'error',
                        title: 'Copy failed',
                        text: 'Billing update link could not be generated. Please try again or contact support.',
                        duration: 5000,
                        error: {
                            message: '[AUDIENCE] failed-to-copy-billing-link',
                            options: {
                                tags: { feature: SENTRY_FEATURES.AUDIENCE },
                                context: { userFanId: customer.id },
                            },
                        },
                    });
                }
                return;
            }

            if (actionId === 'pause' || actionId === 'renew' || actionId === 'cancel') {
                const verb = actionId === 'cancel' ? 'end' : actionId;
                const customerName = customer.fullName ?? 'this customer';
                const description = (
                    <div className='text-center'>
                        Just confirming that you want to {verb} {customerName}&apos;s subscription to{' '}
                        <span className='text-brand-main font-bold'>{subscription.title}</span>. This action cannot be
                        undone.
                    </div>
                );
                setMembershipAction({ subscription, action: actionId, description });
            }
        },
        [copyBillingLinkMutation, customer.fullName, customer.id]
    );

    const handleMembershipConfirm = useCallback(async () => {
        if (!membershipAction) return;

        try {
            await manageMembershipMutation.mutateAsync({
                action: membershipAction.action,
                subscriptionId: membershipAction.subscription.subscriptionId,
            });
            stanNotify({ type: 'success', title: 'Membership updated', duration: 3000 });
            setMembershipAction(null);
            onCustomerUpdated?.();
        } catch (error) {
            stanNotify({
                type: 'error',
                title: 'Unable to update membership',
                text: 'Unable to update membership. Please try again or contact support.',
                duration: 5000,
                error: {
                    message: '[AUDIENCE] failed-to-manage-membership',
                    options: {
                        tags: { feature: SENTRY_FEATURES.AUDIENCE },
                        context: { userFanId: customer.id, error },
                    },
                },
            });
        }
    }, [membershipAction, manageMembershipMutation, onCustomerUpdated, customer.id]);

    const handleSubscriptionActionWithModal = useCallback(
        (subscription: SubscriptionViewModel, actionId: string) => {
            handleSubscriptionAction(subscription, actionId);
            if (actionId === 'pause' || actionId === 'renew' || actionId === 'cancel') {
                membershipModal.open();
            }
        },
        [handleSubscriptionAction, membershipModal]
    );

    const handleMembershipConfirmWithModal = useCallback(async () => {
        await handleMembershipConfirm();
        membershipModal.close();
    }, [handleMembershipConfirm, membershipModal]);

    if (!subscriptions.length) {
        return null;
    }

    return (
        <section className='mt-8 space-y-4'>
            <h2 className='text-dark'>Subscriptions</h2>
            <StanCard className='divide-disabled flex flex-col divide-y-1 !p-0'>
                {subscriptions.map(subscription => {
                    const options: DropdownMenuOption[] = subscription.actions.map(action => ({
                        label: action.label,
                        value: action.id,
                    }));

                    return (
                        <div
                            key={subscription.subscriptionId}
                            className='flex w-full flex-row items-start p-4 md:items-center'
                        >
                            <div className='flex w-full items-start gap-4'>
                                {subscription.subscriptionImage && (
                                    <img
                                        src={subscription.subscriptionImage}
                                        alt={subscription.title}
                                        className='size-12 rounded-md object-cover'
                                    />
                                )}
                                <div className='text-dark flex w-full flex-col gap-1'>
                                    <div className='flex w-full flex-col flex-wrap gap-1 md:flex-row md:items-center'>
                                        <span className='caption-1-bold'>{subscription.title}</span>
                                        <StanTag
                                            label={subscription.statusLabel}
                                            className={cn(subscription.statusClassName, 'w-full md:w-fit')}
                                        />
                                    </div>
                                    <div className='text-gray caption-1-regular flex flex-wrap items-center gap-3'>
                                        <span className='caption-1-bold'>Billing {subscription.intervalLabel}</span>
                                        {subscription.validUntilLabel && (
                                            <span className='text-gray'>{subscription.validUntilLabel}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {options.length > 0 && (
                                <DropdownProvider
                                    dropdownId={`subscription-${subscription.subscriptionId}-menu`}
                                    dropdownName='customer-subscription-actions'
                                >
                                    <StanDropdownMenu
                                        id={`subscription-${subscription.subscriptionId}`}
                                        name={`subscription-${subscription.subscriptionId}`}
                                        options={options}
                                        onSelect={({ value }) => handleSubscriptionActionWithModal(subscription, value)}
                                        rounded
                                    >
                                        <LuEllipsisVertical className='text-gray h-6 w-12' />
                                    </StanDropdownMenu>
                                </DropdownProvider>
                            )}
                        </div>
                    );
                })}
            </StanCard>

            {/* Membership Modal */}
            {membershipAction && (
                <ManageMembershipModal
                    isOpen={membershipModal.isOpen}
                    actionLabel={
                        membershipAction.action === 'cancel'
                            ? 'End Subscription'
                            : `${membershipAction.action.charAt(0).toUpperCase()}${membershipAction.action.slice(1)} Subscription`
                    }
                    description={membershipAction.description}
                    onClose={() => {
                        setMembershipAction(null);
                        membershipModal.close();
                    }}
                    onConfirm={handleMembershipConfirmWithModal}
                    isLoading={manageMembershipMutation.isPending}
                />
            )}
        </section>
    );
}
